import React, { useEffect, useState } from "react";
import { Scatter } from "react-chartjs-2";
import {
  Chart as ChartJS,
  LinearScale,
  PointElement,
  Tooltip,
  Legend,
} from "chart.js";
import styled from "styled-components";
import { Info } from "lucide-react";
import { Tooltip as InfoTooltip } from "react-tooltip";

// Register Chart.js components
ChartJS.register(LinearScale, PointElement, Tooltip, Legend);

const ChartWrapper = styled.div`
  background-color: #f9f9f9;
  width: 100%;
  height: 75vh;
  display: flex;
  flex-direction: column;
  padding: 20px;
`;

const TitleWrapper = styled.div`
  font-family: Arial, sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: bold;
  color: #666;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
`;

const FiltersWrapper = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-bottom: 16px;
`;

const Select = styled.select`
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 14px;
  border: 1px solid #ccc;
`;

const LargestSmallestTransactions = ({ salesData }) => {
  const [filteredData, setFilteredData] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonths, setAvailableMonths] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState("all");
  const [chartData, setChartData] = useState(null);

  const monthNames = [
    "Styczeń", "Luty", "Marzec", "Kwiecień", "Maj", "Czerwiec",
    "Lipiec", "Sierpień", "Wrzesień", "Październik", "Listopad", "Grudzień"
  ];

const calculateTransactionValue = (row) => {
  const val = parseFloat(
    row.K_15 || row.K_19 || row.K_10 || row.K_21 || row.K_17 || row.K_11 || row.K_22
  );
  return isNaN(val) ? 0 : val;
};



  useEffect(() => {
    if (!salesData || salesData.length === 0) return;

    const years = [...new Set(salesData.map((sale) => parseInt(sale.Rok, 10)))].sort((a, b) => b - a);
    setAvailableYears(years);
    if (years.length > 0) setSelectedYear(years[0]);
  }, [salesData]);

  useEffect(() => {
    if (selectedYear) {
      const months = [...new Set(salesData
        .filter((sale) => parseInt(sale.Rok, 10) === selectedYear)
        .map((sale) => parseInt(sale.Miesiac, 10)))].sort((a, b) => a - b);
      setAvailableMonths(months);
    }
  }, [selectedYear, salesData]);

  useEffect(() => {
    if (!selectedYear) return;

    const filtered = salesData.filter(
      (sale) =>
        parseInt(sale.Rok, 10) === selectedYear &&
        (selectedMonth === "all" || parseInt(sale.Miesiac, 10) === Number(selectedMonth))
    );

    setFilteredData(filtered);
  }, [selectedYear, selectedMonth, salesData]);

  useEffect(() => {
    if (!filteredData || filteredData.length === 0) return;

const transactions = filteredData.map((sale, index) => ({
  id: index + 1,
  value: calculateTransactionValue(sale),
  date: sale.Data || "brak daty",
  customer: sale.NazwaKontrahenta || "Brak nazwy",
  document: sale.DowodSprzedazy || "Brak numeru",
}));


    const sorted = [...transactions].sort((a, b) => a.value - b.value);
    const smallest = sorted.slice(0, 5);
    const largest = sorted.slice(-5);

    const allValues = transactions.map((t) => t.value);
    const avg = allValues.reduce((sum, v) => sum + v, 0) / allValues.length;
    const margin = avg * 0.2;
    const avgRange = sorted.filter((t) => t.value >= avg - margin && t.value <= avg + margin);

 setChartData({
  datasets: [
    {
      label: "Najmniejsze transakcje",
      data: smallest.map((t) => ({
        x: t.id,
        y: t.value,
        date: t.date,
        customer: t.customer,
        document: t.document,
      })),
      backgroundColor: "red",
      radius: 6,
    },
    {
      label: "Największe transakcje",
      data: largest.map((t) => ({
        x: t.id,
        y: t.value,
        date: t.date,
        customer: t.customer,
        document: t.document,
      })),
      backgroundColor: "green",
      radius: 6,
    },
    {
      label: "Średnie transakcje",
      data: avgRange.map((t) => ({
        x: t.id,
        y: t.value,
        date: t.date,
        customer: t.customer,
        document: t.document,
      })),
      backgroundColor: "blue",
      radius: 6,
    },
  ],
});

  }, [filteredData]);

  return (
    <ChartWrapper>
      <TitleWrapper>
        Największe i Najmniejsze Transakcje
        <div data-tooltip-id="largest-smallest-tooltip" style={{ cursor: "pointer" }}>
          <Info size={18} />
        </div>
      </TitleWrapper>

      <InfoTooltip
        id="largest-smallest-tooltip"
        place="right"
        style={{ fontSize: "12px", fontFamily: "Arial, sans-serif", color: "#fff" }}
        content={
          <>
            <strong>Wykres rozrzutu transakcji</strong><br />
            Pokazuje 5 największych, 5 najmniejszych<br />
            oraz transakcje zbliżone do średniej dla wybranego okresu.
          </>
        }
      />

      <FiltersWrapper>
        <div>
          <label>Rok:</label>
          <Select value={selectedYear || ""} onChange={(e) => setSelectedYear(Number(e.target.value))}>
            {availableYears.map((year) => (
              <option key={year} value={year}>{year}</option>
            ))}
          </Select>
        </div>
        <div>
          <label>Miesiąc:</label>
          <Select value={selectedMonth} onChange={(e) => setSelectedMonth(e.target.value === "all" ? "all" : Number(e.target.value))}>
            <option value="all">Cały rok</option>
            {availableMonths.map((month) => (
              <option key={month} value={month}>
                {monthNames[month - 1]}
              </option>
            ))}
          </Select>
        </div>
      </FiltersWrapper>

      {chartData ? (
        <Scatter
          data={chartData}
          options={{
            responsive: true,
            maintainAspectRatio: false,
            scales: {
              x: { title: { display: true, text: "Transakcje (ID)" } },
y: {
  beginAtZero: true,
  title: { display: true, text: "Kwota (PLN)" },
  ticks: {
    callback: (value) => Number(value).toLocaleString("pl-PL"),
  },
},

            },
            plugins: {
              legend: { position: "top" },
tooltip: {
  callbacks: {
    label: (context) => {
      const value = context.parsed.y;
      const raw = context.raw || {};

      const lines = [
        `Kwota: ${value.toLocaleString("pl-PL", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })} PLN`,
      ];

      if (raw.customer) {
        lines.push(`Kontrahent: ${raw.customer}`);
      }
      if (raw.document) {
        lines.push(`Dokument: ${raw.document}`);
      }
      if (raw.date) {
        lines.push(`Data: ${raw.date}`);
      }

      // Zwracamy tablicę – każdy element to osobna linia w tooltipie
      return lines;
    },
  },
},

            },
          }}
        />
      ) : (
        <p style={{ textAlign: "center" }}>Brak danych dla wybranego okresu.</p>
      )}
    </ChartWrapper>
  );
};

export default LargestSmallestTransactions;
